/**
 * Keyboard and state management utilities for canvas interactions
 * Extracted from MyCanvas.js to reduce file size
 */

/**
 * Creates keyboard handler for undo/redo shortcuts
 * @param {Function} undo - Undo function
 * @param {Function} redo - Redo function
 * @returns {Function} Handler function for keyboard events
 */
export const createUndoRedoKeyHandler = (undo, redo) => {
  return (e) => {
    // Check if we're in an input field or contentEditable element
    const isInInput =
      e.target.tagName === "INPUT" ||
      e.target.tagName === "TEXTAREA" ||
      e.target.isContentEditable;

    if (isInInput) return; // Don't handle shortcuts in input fields

    if (e.ctrlKey || e.metaKey) {
      switch (e.key.toLowerCase()) {
        case "z":
          e.preventDefault();
          if (e.shiftKey) {
            // Ctrl+Shift+Z or Cmd+Shift+Z for redo
            redo();
          } else {
            // Ctrl+Z or Cmd+Z for undo
            undo();
          }
          break;
      }
    }
  };
};

/**
 * Creates keyboard handler for zoom shortcuts
 * @param {Function} resetZoom - Reset zoom function
 * @param {Function} handleZoom - Handle zoom function
 * @returns {Function} Handler function for keyboard events
 */
export const createZoomKeyHandler = (resetZoom, handleZoom) => {
  return (e) => {
    // Ctrl/Cmd + 0 to reset zoom
    if ((e.ctrlKey || e.metaKey) && e.key === "0") {
      e.preventDefault();
      resetZoom();
    }
    // Ctrl/Cmd + Plus to zoom in
    if ((e.ctrlKey || e.metaKey) && (e.key === "+" || e.key === "=")) {
      e.preventDefault();
      handleZoom(1.1);
    }
    // Ctrl/Cmd + Minus to zoom out
    if ((e.ctrlKey || e.metaKey) && e.key === "-") {
      e.preventDefault();
      handleZoom(0.9);
    }
  };
};

/**
 * Creates keyboard handler for paste shortcuts
 * @param {Function} isTextInputOrContentEditable - Function to check if target is text input
 * @param {Function} handlePasteElements - Function to handle paste elements
 * @returns {Function} Handler function for keyboard events
 */
export const createPasteKeyHandler = (
  isTextInputOrContentEditable,
  handlePasteElements
) => {
  return (e) => {
    // Handle both Windows (Ctrl+V) and macOS (Cmd/Meta+V)
    const isPasteShortcut =
      (e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "v";

    if (!isPasteShortcut) return;

    // If the focus is inside a text input / textarea / content-editable, allow default paste
    if (isTextInputOrContentEditable(e.target)) {
      return;
    }

    // Otherwise, prevent default and paste canvas elements
    e.preventDefault();
    handlePasteElements();
  };
};

/**
 * Creates a hash of the current state for change detection
 * @param {Object} state - Current canvas state
 * @returns {string} JSON string hash of the state
 */
function safeCropDataHash(cropData) {
  if (!cropData) return "";
  try {
    return JSON.stringify(cropData);
  } catch {
    return "";
  }
}

export const createStateHash = (state) => {
  try {
    const elementHashes = {
      texts: (state.texts || [])
        .map((t) => {
          // Create a hash that includes partsByType structure
          const partsHash = t.partsByType
            ? Object.entries(t.partsByType)
                .map(
                  ([typeKey, typeData]) =>
                    `${typeKey}:${typeData.parts?.length || 0}:${
                      typeData.parts?.map((p) => p.id).join(",") || ""
                    }`
                )
                .sort()
                .join("|")
            : "";

          // Include all text properties that can change and should be tracked in history
          return `${t.id}-${t.x}-${t.y}-${t.width || ""}-${t.height || ""}-${
            t.rotation || 0
          }-${t.fontSize || ""}-${t.fontFamily || ""}-${t.fillColor || ""}-${
            t.strokeWidth || 0
          }-${t.stroke || ""}-${t.part || ""}-${t.task || ""}-${t.note || ""}-${
            t.content || ""
          }-${t.plainText || ""}-${t.isHtml || false}-${partsHash}-${
            t.backgroundColor || ""
          }-${t.showBackground || false}`;
        })
        .sort()
        .join("|"),
      rectangles: (state.rectangles || [])
        .map(
          (r) =>
            `${r.id}-${r.x}-${r.y}-${r.width}-${r.height}-${r.rotation || 0}-${
              r.text || ""
            }-${r.fontSize || ""}-${r.textColor || ""}-${r.fontStyle || ""}-${
              r.textDecoration || ""
            }-${r.align || ""}-${r.verticalAlign || ""}-${r.stroke || ""}-${
              r.strokeWidth || 0
            }-${r.strokeOpacity ?? 1}-${r.fill || ""}-${r.fillColor || ""}-${
              r.fillOpacity ?? 1
            }`
        )
        .sort()
        .join("|"),
      circles: (state.circles || [])
        .map(
          (c) =>
            `${c.id}-${c.x}-${c.y}-${c.radius || ""}-${c.radiusX || ""}-${
              c.radiusY || ""
            }-${c.rotation || 0}-${c.text || ""}-${c.fontSize || ""}-${
              c.textColor || ""
            }-${c.fontStyle || ""}-${c.textDecoration || ""}-${c.align || ""}-${
              c.verticalAlign || ""
            }-${c.stroke || ""}-${c.strokeWidth || 0}-${c.strokeOpacity ?? 1}-${
              c.fill || ""
            }-${c.fillColor || ""}-${c.fillOpacity ?? 1}-${
              c.maintainAspectRatio !== undefined ? c.maintainAspectRatio : true
            }`
        )
        .sort()
        .join("|"),
      arrows: (state.arrows || [])
        .map(
          (a) =>
            `${a.id}-${a.x || 0}-${a.y || 0}-${a.points?.join(",") || ""}-${a.stroke || ""}-${
              a.strokeWidth || 0
            }-${a.strokeOpacity ?? 1}-${a.isDashed || false}-${a.angle || 0}-${
              a.rotation || 0
            }`
        )
        .sort()
        .join("|"),
      doubleSidedArrows: (state.doubleSidedArrows || [])
        .map(
          (a) =>
            `${a.id}-${a.x || 0}-${a.y || 0}-${a.points?.join(",") || ""}-${a.stroke || ""}-${
              a.strokeWidth || 0
            }-${a.strokeOpacity ?? 1}-${a.isDashed || false}-${a.angle || 0}-${
              a.rotation || 0
            }`
        )
        .sort()
        .join("|"),
      images: (state.images || [])
        .map(
          (i) => {
            const cropDataHash = safeCropDataHash(i.cropData);
            // Include imageDataUrl and originalImageDataUrl to detect crop/uncrop changes
            const imageDataUrlHash = i.imageDataUrl || "";
            const originalImageDataUrlHash = i.originalImageDataUrl || "";
            return `${i.id}-${i.x}-${i.y}-${i.width}-${i.height}-${i.src}-${
              i.rotation || 0
            }-${i.scaleX || 1}-${i.scaleY || 1}-${i.isOnTopLayer || false}-${
              i.maintainAspectRatio !== undefined ? i.maintainAspectRatio : true
            }-${i.flipHorizontal || false}-${i.flipVertical || false}-${
              i.referenceUrl || ""
            }-${cropDataHash}-${imageDataUrlHash}-${originalImageDataUrlHash}`;
          }
        )
        .sort()
        .join("|"),
      videos: (state.videos || [])
        .map((v) => `${v.id}-${v.x}-${v.y}-${v.width}-${v.height}-${v.src}`)
        .sort()
        .join("|"),
      scribbles: (state.scribbles || [])
        .map((s) => `${s.id}-${s.points?.length || 0}`)
        .sort()
        .join("|"),
      lines: (state.lines || [])
        .map((l) => {
          const startDotHash = l.startDot
            ? `${l.startDot.radius || 0}-${l.startDot.fill || ""}-${
                l.startDot.stroke || ""
              }-${l.startDot.strokeWidth || 0}`
            : "";
          const endDotHash = l.endDot
            ? `${l.endDot.radius || 0}-${l.endDot.fill || ""}-${
                l.endDot.stroke || ""
              }-${l.endDot.strokeWidth || 0}`
            : "";
          // CRITICAL: Include hasStartArrow and hasEndArrow in hash to detect pointer head changes
          // This ensures that changes to arrow flags create distinct history states
          return `${l.id}-${l.x || 0}-${l.y || 0}-${l.points?.join(",") || ""}-${l.stroke || ""}-${
            l.strokeWidth || 0
          }-${l.strokeOpacity ?? 1}-${l.isDashed || false}-${
            l.angle || 0
          }-${l.rotation || 0}-${l.hasStartArrow || false}-${l.hasEndArrow || false}-startDot:${startDotHash}-endDot:${endDotHash}`;
        })
        .sort()
        .join("|"),
      // CRITICAL: Include off-canvas elements in hash to detect changes
      // Without this, adding off-canvas elements doesn't change the hash, causing duplicate state detection
      offCanvasRectangles: (state.offCanvasRectangles || [])
        .map(
          (r) =>
            `${r.id}-${r.x}-${r.y}-${r.width}-${r.height}-${r.rotation || 0}-${
              r.text || ""
            }-${r.fontSize || ""}-${r.textColor || ""}-${r.fontStyle || ""}-${
              r.textDecoration || ""
            }-${r.align || ""}-${r.verticalAlign || ""}-${r.stroke || ""}-${
              r.strokeWidth || 0
            }-${r.strokeOpacity ?? 1}-${r.fill || ""}-${r.fillColor || ""}-${
              r.fillOpacity ?? 1
            }`
        )
        .sort()
        .join("|"),
      offCanvasCircles: (state.offCanvasCircles || [])
        .map(
          (c) =>
            `${c.id}-${c.x}-${c.y}-${c.radius || ""}-${c.radiusX || ""}-${
              c.radiusY || ""
            }-${c.rotation || 0}-${c.text || ""}-${c.fontSize || ""}-${
              c.textColor || ""
            }-${c.fontStyle || ""}-${c.textDecoration || ""}-${c.align || ""}-${
              c.verticalAlign || ""
            }-${c.stroke || ""}-${c.strokeWidth || 0}-${c.strokeOpacity ?? 1}-${
              c.fill || ""
            }-${c.fillColor || ""}-${c.fillOpacity ?? 1}-${
              c.maintainAspectRatio !== undefined ? c.maintainAspectRatio : true
            }`
        )
        .sort()
        .join("|"),
      offCanvasArrows: (state.offCanvasArrows || [])
        .map(
          (a) =>
            `${a.id}-${a.x || 0}-${a.y || 0}-${a.points?.join(",") || ""}-${a.stroke || ""}-${
              a.strokeWidth || 0
            }-${a.strokeOpacity ?? 1}-${a.isDashed || false}-${a.angle || 0}-${
              a.rotation || 0
            }`
        )
        .sort()
        .join("|"),
      offCanvasDoubleSidedArrows: (state.offCanvasDoubleSidedArrows || [])
        .map(
          (a) =>
            `${a.id}-${a.x || 0}-${a.y || 0}-${a.points?.join(",") || ""}-${a.stroke || ""}-${
              a.strokeWidth || 0
            }-${a.strokeOpacity ?? 1}-${a.isDashed || false}-${a.angle || 0}-${
              a.rotation || 0
            }`
        )
        .sort()
        .join("|"),
      offCanvasLines: (state.offCanvasLines || [])
        .map((l) => {
          const startDotHash = l.startDot
            ? `${l.startDot.radius || 0}-${l.startDot.fill || ""}-${
                l.startDot.stroke || ""
              }-${l.startDot.strokeWidth || 0}`
            : "";
          const endDotHash = l.endDot
            ? `${l.endDot.radius || 0}-${l.endDot.fill || ""}-${
                l.endDot.stroke || ""
              }-${l.endDot.strokeWidth || 0}`
            : "";
          return `${l.id}-${l.x || 0}-${l.y || 0}-${l.points?.join(",") || ""}-${l.stroke || ""}-${
            l.strokeWidth || 0
          }-${l.strokeOpacity ?? 1}-${l.isDashed || false}-${
            l.angle || 0
          }-${l.rotation || 0}-${l.hasStartArrow || false}-${l.hasEndArrow || false}-startDot:${startDotHash}-endDot:${endDotHash}`;
        })
        .sort()
        .join("|"),
      offCanvasTexts: (state.offCanvasTexts || [])
        .map((t) => {
          const partsHash = t.partsByType
            ? Object.entries(t.partsByType)
                .map(
                  ([typeKey, typeData]) =>
                    `${typeKey}:${typeData.parts?.length || 0}:${
                      typeData.parts?.map((p) => p.id).join(",") || ""
                    }`
                )
                .sort()
                .join("|")
            : "";
          return `${t.id}-${t.x}-${t.y}-${t.width || ""}-${t.height || ""}-${
            t.rotation || 0
          }-${t.fontSize || ""}-${t.fontFamily || ""}-${t.fillColor || ""}-${
            t.strokeWidth || 0
          }-${t.stroke || ""}-${t.part || ""}-${t.task || ""}-${t.note || ""}-${
            t.content || ""
          }-${t.plainText || ""}-${t.isHtml || false}-${partsHash}-${
            t.backgroundColor || ""
          }-${t.showBackground || false}`;
        })
        .sort()
        .join("|"),
      offCanvasImages: (state.offCanvasImages || [])
        .map(
          (i) => {
            const cropDataHash = safeCropDataHash(i.cropData);
            const imageDataUrlHash = i.imageDataUrl || "";
            const originalImageDataUrlHash = i.originalImageDataUrl || "";
            return `${i.id}-${i.x}-${i.y}-${i.width}-${i.height}-${i.src}-${
              i.rotation || 0
            }-${i.scaleX || 1}-${i.scaleY || 1}-${i.isOnTopLayer || false}-${
              i.maintainAspectRatio !== undefined ? i.maintainAspectRatio : true
            }-${i.flipHorizontal || false}-${i.flipVertical || false}-${
              i.referenceUrl || ""
            }-${cropDataHash}-${imageDataUrlHash}-${originalImageDataUrlHash}`;
          }
        )
        .sort()
        .join("|"),
      offCanvasScribbles: (state.offCanvasScribbles || [])
        .map((s) => `${s.id}-${s.points?.length || 0}`)
        .sort()
        .join("|"),
      offCanvasVideos: (state.offCanvasVideos || [])
        .map((v) => `${v.id}-${v.x}-${v.y}-${v.width}-${v.height}-${v.src}`)
        .sort()
        .join("|"),
      canvasHeight: state.canvasHeight || "80", // Include canvas height in hash
    };
    return JSON.stringify(elementHashes);
  } catch (error) {
    return JSON.stringify(state);
  }
};
